import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { bulkUpdateStudentFeedbacks } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { StudentFeedback, UpdateStudentFeedbackOptions } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

export interface StudentBulkUpdateProps {
  confirmMessage: string;
  options: UpdateStudentFeedbackOptions;
  onSuccess?: () => void;
  studentFeedbacks: Pick<StudentFeedback, 'id'>[];
  title: string;
  successToastTitle?: (updatedCount: number) => string;
  confirmBtnLabel?: string;
  confirmCountNoun?: string;
}

const formSchema = z.object({});

export const StudentFeedbackBulkUpdate = ({
  confirmMessage,
  options,
  onSuccess,
  studentFeedbacks,
  title,
  successToastTitle = (count) => `${count} review note(s) were updated`,
  confirmBtnLabel = 'Confirm',
  confirmCountNoun = 'review note(s)',
}: StudentBulkUpdateProps) => {
  const { toast } = useToast();
  const { mutateAsync } = useMutation({ mutationFn: bulkUpdateStudentFeedbacks });

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({ studentFeedbackIds: studentFeedbacks.map(({ id }) => id), options })
      .then(() => {
        toast({
          title: successToastTitle(studentFeedbacks.length),
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'There was an error. Please try again.',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle={title}
      formSchema={formSchema}
      trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>{title}</DropdownMenuItem>}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: studentFeedbacks.length === 0,
        variant: 'destructive',
      }}
      confirmBtnLabel={confirmBtnLabel}
    >
      {() => {
        return (
          <div className="my-6">
            {studentFeedbacks.length === 0 ? (
              <div>None of the selected student feedbacks are eligible.</div>
            ) : (
              <div>{`${confirmMessage} ${studentFeedbacks.length} ${confirmCountNoun}?`}</div>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
